import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";

import {
  getAdminLookups,
  saveAdminLookup,
  deleteAdminLookup,
} from "../../services/adminApi";

import ReusableAdminTable from "./ReusableAdminTable";
import useLookupColumns from "./lookups/components/useLookupColumns";
import LookupFormView from "./lookups/components/LookupFormView";
import "./AdminLookupPage.css";

const AdminLookupPage = () => {
  const [lookups, setLookups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [groupName, setGroupName] = useState("");
  const [items, setItems] = useState([
    {
      value: "",
      label: "",
    },
  ]);
  const [editingId, setEditingId] = useState(null);

  const fetchLookups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminLookups();
      setLookups(res?.data || []);
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load lookups",
        confirmButtonColor: "#c30000",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        value: "",
        label: "",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const resetForm = () => {
    setGroupName("");
    setItems([
      {
        value: "",
        label: "",
      },
    ]);
    setEditingId(null);
    setView("list");
  };

  const handleSave = async () => {
    if (!groupName) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Group Name is required",
        confirmButtonColor: "#c30000",
      });
      return;
    }

    try {
      await saveAdminLookup({
        lookupGroup: groupName,
        dataList: items,
      });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Lookup saved successfully",
        confirmButtonColor: "#2563eb",
      });

      resetForm();
      fetchLookups();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: "Unable to save lookup",
        confirmButtonColor: "#c30000",
      });
    }
  };

  const handleEdit = useCallback((lk) => {
    setGroupName(lk.lookupGroup);
    setItems(
      lk.lookupValue?.data?.map((d) => ({
        value: d.lookupValue,
        label: d.lookupLabel,
      })) || []
    );
    setEditingId(lk.id);
    setView("form");
  }, []);

  const handleDelete = useCallback(async (lookupGroup) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete ${lookupGroup}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c30000",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteAdminLookup(lookupGroup);
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Lookup deleted successfully",
        confirmButtonColor: "#2563eb",
      });
      fetchLookups();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Unable to delete lookup",
        confirmButtonColor: "#c30000",
      });
    }
  }, [fetchLookups]);

  const columns = useLookupColumns({ handleEdit, handleDelete });

  return (
    <div className="admin-lookup-page">
      {view === "list" ? (
        <ReusableAdminTable
          data={lookups}
          columns={columns}
          isLoading={loading}
          title="Lookup Registry"
          onAddNew={() => setView("form")}
          addNewButtonText="New Lookup"
          searchPlaceholder="Search lookups..."
          exportCsvFilename="lookup-registry.csv"
        />
      ) : (
        <LookupFormView
          editingId={editingId}
          groupName={groupName}
          setGroupName={setGroupName}
          items={items}
          handleItemChange={handleItemChange}
          handleRemoveItem={handleRemoveItem}
          handleAddItem={handleAddItem}
          handleSave={handleSave}
          resetForm={resetForm}
        />
      )}
    </div>
  );
};

export default AdminLookupPage;